import React from 'react';
import Seo from '../Seo/Seo';
import './DeleteAccount.css';

/**
 * Public account-deletion instructions.
 *
 * Google Play requires apps that support account creation to publish a
 * deletion route reachable *without* signing in, so this deliberately lives
 * outside the authenticated area. The deletion itself still happens in
 * Edit Profile; this page explains the process and covers people who can no
 * longer log in.
 */
const DeleteAccount = () => {
  return (
    <>
      <Seo
        title="Delete Your Account | ZealFC"
        description="How to permanently delete your ZealFC account and what happens to your data."
        canonicalPath="/delete-account"
      />
      <div className="delete-account">
        <h1>Delete Your Account</h1>
        <p><strong>Last updated:</strong> September 19, 2026</p>

        <p>
          You can permanently delete your ZealFC account and the personal data attached
          to it at any time. Deletion is immediate and cannot be undone.
        </p>

        <h2>Delete From Your Account</h2>
        <p>On the website or in the ZealFC mobile app:</p>
        <ol>
          <li>Sign in and open <strong>Profile</strong>.</li>
          <li>Open <strong>Edit Profile</strong>.</li>
          <li>Choose <strong>Delete Account</strong>.</li>
          <li>
            We email a confirmation code to your registered address. Enter it to confirm.
            The code is required so that nobody else can delete your account.
          </li>
        </ol>

        <h2>If You Cannot Sign In</h2>
        <p>
          Email <a href="mailto:contact@zealfc.com">contact@zealfc.com</a> from the address
          you registered with and ask us to delete your account. We may need to confirm your
          identity before acting on the request, and we will confirm by email once it is done.
        </p>

        <h2>What Is Deleted</h2>
        <ul>
          <li>Your account profile — name, email address, phone number, position and profile photo.</li>
          <li>Your sign-in credentials, so the account can no longer be used to log in.</li>
          <li>Your registrations for any upcoming games, freeing your place for other players.</li>
          <li>Any active subscription, which is cancelled as part of the deletion.</li>
        </ul>

        <h2>What Is Kept</h2>
        <ul>
          <li>
            Payment and invoicing records held by our payment processor, Stripe, where we are
            legally required to keep them for tax and accounting purposes.
          </li>
          <li>
            Records of completed games may be retained in anonymised form — no longer linked
            to you — so that other players&apos; match history and statistics stay intact.
          </li>
        </ul>
        <p>
          Anything we retain is kept only for as long as necessary or as required by law, as
          described in our <a href="/privacy">Privacy Policy</a>.
        </p>

        <h2>How Long It Takes</h2>
        <p>
          Deletions made from Edit Profile take effect immediately. Requests sent by email are
          handled within 30 days, and usually much sooner.
        </p>

        <h2>Contact</h2>
        <p>
          Questions about deletion or your data:{' '}
          <a href="mailto:contact@zealfc.com">contact@zealfc.com</a>
        </p>
      </div>
    </>
  );
};

export default DeleteAccount;
